import * as THREE from 'three';
import {PerspectiveCamera} from '../media-engine/camera';
import {Model} from '../media-engine/model';
import {Hud, MobileHud, NaiveMobileHud} from '../media-engine/hud';
import {PointerLock} from '../media-engine/controls/pointer-lock';
import {Orientation, NaiveOrientation} from '../media-engine/controls/orientation';
import {Bookmark} from '../media-engine/bookmark';

interface EngineParameters {
    position: THREE.Vector3;
    box: THREE.Box3;
    clearColor?: number;
    startAtFirstInteraction?: boolean;
    device: 'Desktop' | 'Mobile1' | 'Mobile';
}

export class Engine {
    onHoverCallbacks: ((name?: string) => void)[];
    onMoveCallbacks: ((name?: string) => void)[];
    onFirstCallbacks: (() => void)[];
    startAtFirstInteraction: boolean;
    firstInteractionOccured: boolean;
    bookmarkClicked: boolean;
    running: boolean;
    camera: PerspectiveCamera;
    scene: THREE.Scene;
    model: Model;
    raycaster: THREE.Raycaster;
    hud: Hud;
    controls: PointerLock | Orientation;
    realRenderer: THREE.WebGLRenderer;
    renderer: THREE.Renderer;

    constructor(params: EngineParameters) {
        params.clearColor = params.clearColor || 0xabcdef;
        this.startAtFirstInteraction = params.startAtFirstInteraction || false;
        this.firstInteractionOccured = false;

        this.onHoverCallbacks = [];
        this.onMoveCallbacks = [];
        this.onFirstCallbacks = [];
        this.bookmarkClicked = false;

        this.running = false;

        // Initialize renderer
        this.camera = new PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 1000);
        this.camera.position.copy(params.position);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));

        this.scene = new THREE.Scene();

        // Some light for bookmarks
        let directionalLight = new THREE.DirectionalLight(0xb2b2b2, 0.5);
        directionalLight.position.set(-10, -5, -7);
        this.scene.add(directionalLight);
        directionalLight = new THREE.DirectionalLight(0x7f7f7f, 0.5);
        directionalLight.position.set(10, -5, -7);
        this.scene.add(directionalLight);
        let light = new THREE.AmbientLight(0xb2b2b2); // soft white light
        this.scene.add(light);

        this.model = new Model();

        let factor = 300 / Math.max(
            params.box.max.x - params.box.min.x,
            params.box.max.y - params.box.min.y,
            params.box.max.z - params.box.min.z);

        this.model.position.set(
            - factor * (params.box.max.x + params.box.min.x) / 2,
            - factor * (params.box.max.y + params.box.min.y) / 2,
            - factor * (params.box.max.z + params.box.min.z) / 2
        );
        this.model.scale.set(factor, factor, factor);

        this.scene.add(this.model);

        this.realRenderer = new THREE.WebGLRenderer();
        this.realRenderer.setClearColor(params.clearColor);
        this.renderer = this.realRenderer;

        this.raycaster = new THREE.Raycaster();
        window.addEventListener('resize', () => this.onWindowResize());

        // Initialize interaction
        switch (params.device) {
            case 'Desktop':
                this.hud = new Hud();
                this.controls = new PointerLock(this.camera, 0.002, 0.001,);
                this.controls.addEventListener('enabled', () => {if (this.hud !== undefined) this.hud.setClickRequired(false);});
                this.controls.addEventListener('disabled', () => {if (this.hud !== undefined) this.hud.setClickRequired(true);});
                document.addEventListener('pointerdown', (e) => this.onPointerDown(e));
                break;

            case 'Mobile':
                this.hud = new MobileHud();
                this.controls = new Orientation(this.camera, this.hud as MobileHud);
                document.addEventListener('pointerdown', (e) => this.onPointerDown(e));
                document.addEventListener('pointerup', () => {
                    document.body.requestFullscreen();
                    if (this.hud !== undefined) {
                        this.hud.setClickRequired(false);
                    }
                });
                break;

            case 'Mobile1':
                this.hud = new NaiveMobileHud();
                this.controls = new NaiveOrientation(this.camera, this.hud as MobileHud);
                document.addEventListener('pointerdown', (e) => this.onPointerDown(e));
                document.addEventListener('pointerup', () => {
                    document.body.requestFullscreen();
                    if (this.hud !== undefined) {
                        this.hud.setClickRequired(false);
                    }
                });
                break;
        }

        this.onWindowResize();

    }

    addEventListener(name: "hover" | "move" | "first", callback: (name?: string) => void) {
        switch (name) {
            case "hover":
                this.onHoverCallbacks.push(callback);
                break;

            case "move":
                this.onMoveCallbacks.push(callback);
                break;

            case "first":
                this.onFirstCallbacks.push(callback);
                break;
        }
    }

    callOnHoverCallbacks(name?: string) {
        for (let c of this.onHoverCallbacks) {
            c(name);
        }
    }

    callOnMoveCallbacks(name?: string) {
        for (let c of this.onMoveCallbacks) {
            c(name);
        }
    }

    callOnFirstCallbacks() {
        for (let c of this.onFirstCallbacks) {
            c();
        }
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(window.innerWidth, window.innerHeight);
        if (this.hud !== undefined) {
            this.hud.setSize(window.innerWidth, window.innerHeight);
        }
    }

    render() {
        this.renderer.render(this.scene, this.camera);
        if (this.hud !== undefined) {
            this.hud.render();
        }
    }

    addToElement(element: HTMLElement) {
        element.appendChild(this.realRenderer.domElement);
        if (this.hud !== undefined) {
            element.appendChild(this.hud.domElement);
        }
    }

    animate() {
        this.render();
        if (this.running) {
            requestAnimationFrame(() => this.animate());
        }
        this.update();
        this.render();
    }

    update() {
        this.controls.update();

        for (let bookmark of this.model.bookmarks.children) {
            (bookmark as Bookmark).update(this.camera);
        }

        if (!(this.hud instanceof MobileHud)) {
            let intersection = this.getIntersection(0, 0);
            if (intersection !== null) {
                this.hud.setHighlight(true);

                if (intersection instanceof Bookmark) {
                    this.callOnHoverCallbacks(intersection.name);
                    this.hud.setThumbnail(this.model.getThumbnail(intersection.name));
                }
            } else {
                this.hud.setHighlight(false);
                this.hud.setThumbnail();
            }
        }

        if (this.hud instanceof MobileHud) {
            if (this.bookmarkClicked && this.controls.moving === undefined) {
                this.bookmarkClicked = false;
                this.callOnMoveCallbacks();
            }
        }
    }

    kill() {
        this.running = false;
    }

    start() {
        if (!this.startAtFirstInteraction) {
            this.running = true;
        }
        this.animate();
    }

    onPointerDown(event: PointerEvent) {

        if (!this.firstInteractionOccured) {
            this.callOnFirstCallbacks();
            this.firstInteractionOccured = true;
            if (this.startAtFirstInteraction) {
                this.running = true;
            }
            this.animate();
        }

        let x = event.offsetX / window.innerWidth * 2 - 1;
        let y = - event.offsetY / window.innerHeight * 2 + 1;

        // If pointerlock controls, don't consider x and y, the click is on the center of the screen
        if (this.controls instanceof PointerLock && this.controls.enabled) {
            x = 0;
            y = 0;
        }

        let intersection = this.getIntersection(x, y);

        if (intersection === null) {
            return;
        }

        if (!(intersection instanceof Bookmark)) {
            return;
        }

        let bm = {
            position: intersection.realPosition,
            target: intersection.target,
        };

        let pos = this.model.localToWorld(bm.position.clone());
        let tar = this.model.localToWorld(bm.target.clone());
        this.controls.moveTo(pos, tar, 1000);
        this.callOnMoveCallbacks(intersection.name);
    }


    firstIntersection(x: number, y: number, camera: PerspectiveCamera, objects: THREE.Object3D[]) {
        let click = new THREE.Vector2(x, y);
        this.raycaster.setFromCamera(click, camera);
        let intersections = this.raycaster.intersectObjects(objects, true);
        for (let i = 0; i < intersections.length; i++) {
            if (intersections[i].object.visible) {
                return intersections[i].object;
            }
        }
    }

    getIntersection(x: number, y: number) : null | THREE.Object3D {
        let intersection = this.firstIntersection(x, y, this.camera, [this.model.bookmarks]);
        if (intersection !== undefined) {
            if (intersection === this.firstIntersection(x, y, this.camera, [this.scene])) {
                let bookmark = intersection;
                while (!(bookmark instanceof Bookmark)) {
                    let parent = bookmark.parent;
                    if (parent === null) {
                        return null;
                    }
                    bookmark = parent;
                }
                return bookmark;
            }
        }

        return null;
    }

}

export function isDeviceMobile(): boolean {
    let check = false;
    (function (a) {if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true;})(navigator.userAgent || navigator.vendor || (window as any).opera);
    return check;
}

function transformToAssocArray(prmstr: string): {[id: string]: string} {
    let params: {[id: string]: string} = {};
    let prmarr = prmstr.split("&");
    for (let i = 0; i < prmarr.length; i++) {
        let tmparr = prmarr[i].split("=");
        params.device = params[tmparr[0]] = tmparr[1];
    }
    return params;
}

export function getSearchParameters(): {[id: string]: string} {
    let prmstr = window.location.search.substr(1);
    return prmstr != null && prmstr != "" ? transformToAssocArray(prmstr) : {};
}
